import {
  AppBar,
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LULogo from "../../assets/LU-2026--Main-Revised.png";

export function FloatingLuBadge() {
  return (
    <Paper
      elevation={8}
      sx={{
        position: "fixed",
        bottom: "calc(20px + env(safe-area-inset-bottom, 0px))",
        right: "calc(25px + env(safe-area-inset-right, 0px))",
        zIndex: 9999,
        px: 2,
        py: 1,
        borderRadius: 30,
        border: "1.5px solid rgba(255,0,0,0.27)",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        userSelect: "none",
      }}
    >
      <Box component="img" src={LULogo} alt="LU" sx={{ height: 20, objectFit: "contain" }} />
      <Typography variant="caption" sx={{ fontWeight: 800, letterSpacing: "0.5px", textTransform: "uppercase" }}>
        Secured by <Box component="span" sx={{ color: "#FF0000" }}>LU</Box>
      </Typography>
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#10b981", boxShadow: "0 0 8px #10b981" }} />
    </Paper>
  );
}

export function PortalNavBar({ brand, industry, onHome, user, profileMenu, themeColors }) {
  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        bgcolor: industry === "corporate" ? "rgba(255,255,255,0.02)" : "transparent",
        color: "text.primary",
        borderBottom: 1,
        borderColor: `${themeColors.text}11`,
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          px: { xs: 1.5, sm: 3, md: 7.5 },
          py: { xs: 1.25, md: 2 },
          minHeight: { xs: "auto", md: 72 },
          width: "100%",
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: { xs: 1, md: 2 },
        }}
      >
        <Typography
          variant="h6"
          component="div"
          sx={{
            fontWeight: 900,
            letterSpacing: "-0.5px",
            fontSize: { xs: 16, md: 20 },
            lineHeight: 1,
            m: 0,
            display: "flex",
            alignItems: "center",
            flexShrink: 0,
          }}
        >
          {brand.toUpperCase()}
        </Typography>

        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            flexWrap: "wrap",
            width: { xs: "100%", md: "auto" },
            justifyContent: { xs: "flex-start", md: "flex-end" },
            rowGap: 1,
          }}
        >
          <Button
            variant="outlined"
            size="small"
            onClick={onHome}
            startIcon={<ArrowBackIcon fontSize="small" />}
            sx={{
              color: themeColors.text,
              borderColor: `${themeColors.text}33`,
              fontWeight: 600,
              lineHeight: 1.5,
              whiteSpace: "nowrap",
              flexShrink: 0,
              fontSize: { xs: 12, sm: 14 },
              px: { xs: 1.5, sm: 2 },
            }}
          >
            Home Page
          </Button>
          <Typography
            component={Link}
            href="#solutions"
            underline="none"
            variant="body2"
            sx={{
              fontWeight: 500,
              opacity: 0.7,
              color: "inherit",
              lineHeight: 1,
              display: { xs: "none", sm: "flex" },
              alignItems: "center",
              flexShrink: 0,
            }}
          >
            Solutions
          </Typography>
          {user && profileMenu}
        </Stack>
      </Toolbar>
    </AppBar>
  );
}

export function ProfileChip({ displayName, themeColors, isOpen, onClick }) {
  return (
    <Paper
      elevation={0}
      onClick={onClick}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: { xs: 0.75, sm: 1 },
        px: { xs: 1.25, sm: 2 },
        py: { xs: 0.75, sm: 1 },
        maxWidth: { xs: "100%", sm: "none" },
        bgcolor: `${themeColors.primary}15`,
        border: `1px solid ${themeColors.primary}33`,
        borderRadius: 1,
        cursor: "pointer",
        userSelect: "none",
        transition: "background 0.2s",
        "&:hover": { bgcolor: `${themeColors.primary}22` },
      }}
    >
      <Avatar sx={{ width: 28, height: 28, bgcolor: themeColors.primary, fontSize: 13, fontWeight: 800 }}>
        {displayName.charAt(0).toUpperCase()}
      </Avatar>
      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: { xs: 13, sm: 14 }, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: { xs: 100, sm: "none" } }}>
        {displayName}
      </Typography>
      <Chip label="✓ Verified" size="small" sx={{ height: 22, fontSize: { xs: 10, sm: 11 }, fontWeight: 700, bgcolor: "rgba(16,185,129,0.1)", color: "#10b981", flexShrink: 0 }} />
      <Typography variant="caption" sx={{ opacity: 0.5 }}>
        {isOpen ? "▲" : "▼"}
      </Typography>
    </Paper>
  );
}

export function ProfileDropdown({ displayName, user, themeColors, onSignOutClick, onClose, isDark }) {
  const rows = [
    { icon: "👤", label: "Full Name", value: (() => { const n = user?.full_name || user?.fullName || user?.name; return (!n || (n.length >= 32 && !n.includes(" "))) ? null : n; })() || displayName },
    { icon: "✉️", label: "Email", value: user?.email },
    { icon: "📱", label: "Phone", value: user?.phone || user?.phone_number || user?.mobile },
    { icon: "🏷️", label: "Username", value: user?.username || user?.user_name },
    { icon: "📅", label: "Member Since", value: user?.created_at ? new Date(user.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }) : null },
    { icon: "🔒", label: "Auth Method", value: "LegitimatelyU Biometric" },
  ].filter((row) => row.value);

  return (
    <Paper
      elevation={12}
      sx={{
        position: "absolute",
        top: "calc(100% + 12px)",
        right: 0,
        zIndex: 5000,
        width: 320,
        bgcolor: isDark ? "#0f172a" : "#fff",
        borderRadius: 2,
        border: `1px solid ${themeColors.primary}22`,
        overflow: "hidden",
      }}
    >
      <Box sx={{ bgcolor: themeColors.primary, p: "20px 24px", display: "flex", alignItems: "center", gap: 1.75 }}>
        <Avatar sx={{ width: 48, height: 48, bgcolor: "rgba(255,255,255,0.2)", color: "#fff", fontWeight: 900, fontSize: 20, border: "2px solid rgba(255,255,255,0.3)" }}>
          {displayName.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 16, lineHeight: 1.2 }}>{displayName}</Typography>
          <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", mt: 0.5, display: "block" }}>LegitimatelyU Verified</Typography>
        </Box>
        <Chip label="✓ Active" size="small" sx={{ bgcolor: "rgba(16,185,129,0.3)", border: "1px solid rgba(16,185,129,0.5)", color: "#6ee7b7", fontWeight: 700 }} />
      </Box>

      <Stack spacing={1.5} sx={{ p: "16px 24px" }}>
        {rows.map((row, i) => (
          <Stack key={i} direction="row" spacing={1.25} sx={{ alignItems: "flex-start" }}>
            <Typography sx={{ fontSize: 15, flexShrink: 0 }}>{row.icon}</Typography>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ fontWeight: 600, opacity: 0.45, textTransform: "uppercase", letterSpacing: "0.5px", display: "block" }}>
                {row.label}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: "break-all" }}>{row.value}</Typography>
            </Box>
          </Stack>
        ))}
      </Stack>

      <Divider sx={{ borderColor: `${themeColors.primary}15` }} />
      <Box sx={{ p: "12px 24px 20px", display: "flex", gap: 1.25 }}>
        <Button fullWidth onClick={onSignOutClick} sx={{ py: 1.25, bgcolor: isDark ? "rgba(239, 68, 68, 0.15)" : "#ff000012", color: isDark ? "#f87171" : "#dc2626", border: isDark ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid #dc262622", fontWeight: 700, fontSize: 13, "&:hover": { bgcolor: isDark ? "rgba(239, 68, 68, 0.25)" : "#ff000020" } }}>
          Sign Out
        </Button>
        <Button fullWidth onClick={onClose} sx={{ py: 1.25, bgcolor: isDark ? "rgba(148, 163, 184, 0.15)" : `${themeColors.primary}12`, color: isDark ? "#cbd5e1" : themeColors.primary, border: isDark ? "1px solid rgba(148, 163, 184, 0.3)" : `1px solid ${themeColors.primary}22`, fontWeight: 700, fontSize: 13, "&:hover": { bgcolor: isDark ? "rgba(148, 163, 184, 0.25)" : `${themeColors.primary}20` } }}>
          Close
        </Button>
      </Box>
    </Paper>
  );
}

function SidebarShell({ dark, themeColors, brand, icon, children, ssoText, footer }) {
  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: dark ? "#0f172a" : "#fff",
        borderRight: dark ? "1px solid rgba(99,102,241,0.2)" : `1px solid ${themeColors.primary}15`,
        borderRadius: { xs: 1.5, lg: 0 },
        border: { xs: dark ? "1px solid rgba(99,102,241,0.2)" : "1px solid #e2e8f0", lg: "none" },
        boxShadow: { xs: "0 4px 16px rgba(2,6,23,0.06)", lg: "none" },
        p: { xs: 1.25, sm: 1.5, lg: "40px 20px" },
        mx: { xs: 0, lg: 0 },
        mb: { xs: 0, lg: 0 },
        overflow: { lg: "hidden" },
        scrollbarWidth: "none",
        msOverflowStyle: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", mb: { xs: 2, lg: 4 }, flexShrink: 0 }}>
        <Typography sx={{ fontSize: { xs: 16, lg: 20 } }}>{icon}</Typography>
        <Typography sx={{ fontSize: { xs: 16, lg: 20 }, fontWeight: 900, color: dark ? "#a855f7" : themeColors.primary }}>
          {brand}
        </Typography>
      </Stack>

      <List
        disablePadding
        sx={{
          flex: { lg: "1 1 auto" },
          minHeight: { lg: 0 },
          overflowY: { lg: "auto" },
          gap: { xs: 0.5, lg: 1 },
          display: "flex",
          flexDirection: "column",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {children}
      </List>

      {footer ?? (
        <Paper
          elevation={0}
          sx={{
            mt: { xs: 2.5, sm: 3.5, lg: "auto" },
            pt: { xs: 0.5, lg: 0 },
            flexShrink: 0,
            bgcolor: dark ? "rgba(30,41,59,0.5)" : "#f8fafc",
            border: dark ? "1px solid rgba(99,102,241,0.15)" : "1px solid #e2e8f0",
            borderRadius: 1.5,
          }}
        >
          <Stack
            spacing={1.5}
            sx={{ alignItems: "center", px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 2.75, lg: 3 }, textAlign: "center" }}
          >
            <Typography sx={{ fontSize: { xs: 24, sm: 32 }, lineHeight: 1 }} aria-hidden>
              🛡️
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 800,
                fontSize: 12,
                letterSpacing: "0.02em",
                color: dark ? "#f8fafc" : themeColors.text,
                display: "block",
                lineHeight: 1.3,
              }}
            >
              Biometric SSO
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: dark ? "#94a3b8" : "#64748b",
                lineHeight: 1.5,
                display: "block",
                px: 0.5,
                maxWidth: 220,
              }}
            >
              {ssoText}
            </Typography>
          </Stack>
        </Paper>
      )}
    </Paper>
  );
}

export function LockedSidebar({ shell, themeColors }) {
  return (
    <SidebarShell dark={shell.darkSidebar} themeColors={themeColors} brand={shell.brand} icon={shell.icon} ssoText={shell.ssoText}>
      {shell.lockedNav.map((item) => (
        <ListItemButton
          key={item}
          disabled
          sx={{
            borderRadius: 1,
            py: { xs: 0.875, lg: 1.5 },
            px: { xs: 1.5, lg: 2.25 },
            minHeight: { xs: 40, lg: 48 },
            opacity: 1,
            "&.Mui-disabled": { opacity: 1 },
          }}
        >
          <ListItemText primary={item} slotProps={{ primary: { fontWeight: 700, fontSize: 13, sx: { fontSize: { xs: 13, lg: 14 }, color: shell.darkSidebar ? "#64748b" : "#94a3b8" } } }} />
          <LockOutlinedIcon sx={{ fontSize: { xs: 13, lg: 14 }, color: "#eab308" }} />
        </ListItemButton>
      ))}
    </SidebarShell>
  );
}

export function AuthSidebar({ shell, themeColors, activeIndex = 0 }) {
  return (
    <SidebarShell dark={shell.darkSidebar} themeColors={themeColors} brand={shell.brand} icon={shell.icon} ssoText={shell.ssoText} footer={null}>
      {shell.authNav.map((item, idx) => (
        <ListItemButton
          key={item}
          selected={idx === activeIndex}
          sx={{
            borderRadius: 1,
            py: { xs: 0.875, lg: 1.5 },
            px: { xs: 1.5, lg: 2.25 },
            bgcolor: idx === activeIndex ? (shell.darkSidebar ? "rgba(99,102,241,0.15)" : `${themeColors.primary}15`) : "transparent",
            color: idx === activeIndex ? (shell.darkSidebar ? "#a855f7" : themeColors.primary) : (shell.darkSidebar ? "#64748b" : "#64748b"),
            minHeight: { xs: 40, lg: 48 },
            "&.Mui-selected": {
              bgcolor: idx === activeIndex ? (shell.darkSidebar ? "rgba(99,102,241,0.15)" : `${themeColors.primary}15`) : "transparent",
            },
            "&:hover": { color: shell.darkSidebar ? "#a855f7" : themeColors.primary },
          }}
        >
          <ListItemText primary={item} slotProps={{ primary: { fontWeight: 700, fontSize: 13, sx: { fontSize: { xs: 13, lg: 14 } } } }} />
        </ListItemButton>
      ))}
    </SidebarShell>
  );
}

export function PortalHeroBanner({ shell, themeColors, authPrompt, onLogin, illustration }) {
  return (
    <Paper
      elevation={4}
      sx={{
        background: `linear-gradient(135deg, ${themeColors.primary}, ${themeColors.accent})`,
        p: { xs: "16px 14px 20px", sm: "24px 20px 28px", md: 5 },
        borderRadius: { xs: "12px", sm: themeColors.borderRadius, md: themeColors.borderRadius },
        color: "#fff",
        boxShadow: `0 10px 25px ${themeColors.primary}26`,
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", md: "center" },
        gap: { xs: 0, md: 4 },
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0, zIndex: 1 }}>
        <Chip
          label={`${shell.heroBadge.icon} ${shell.heroBadge.text}`}
          sx={{
            mb: 2,
            bgcolor: "rgba(255,255,255,0.15)",
            color: "#fff",
            fontWeight: 700,
            fontSize: { xs: 11, md: 12 },
            height: "auto",
            py: 0.75,
            border: "1px solid rgba(255,255,255,0.2)",
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            "& .MuiChip-label": { px: 1.75 },
          }}
        />
        <Typography
          variant="h3"
          sx={{
            fontWeight: 900,
            letterSpacing: "-0.5px",
            lineHeight: 1.15,
            fontSize: { xs: "1.25rem", sm: "1.65rem", md: "2.4rem" },
          }}
        >
          {shell.heroTitle}
        </Typography>
        <Typography sx={{ opacity: 0.85, fontSize: { xs: 12, sm: 14, md: 15 }, mt: 1, maxWidth: 600, lineHeight: 1.5 }}>
          {authPrompt}
        </Typography>
        <Button
          variant="contained"
          onClick={onLogin}
          sx={{
            mt: { xs: 1.75, md: 2.5 },
            px: { xs: 2.5, sm: 3.5 },
            py: { xs: 1, sm: 1.5 },
            fontSize: { xs: 13, sm: 14 },
            bgcolor: "#fff",
            color: themeColors.primary,
            fontWeight: 800,
            "&:hover": { bgcolor: "#f8fafc" },
          }}
        >
          Login
        </Button>
      </Box>
      {illustration && (
        <Box
          component="img"
          src={illustration}
          alt=""
          sx={{
            width: 300,
            maxWidth: { xs: 0, md: "40%" },
            display: { xs: "none", md: "block" },
            height: "auto",
            flexShrink: 0,
            objectFit: "contain",
          }}
        />
      )}
    </Paper>
  );
}

export function PortalShell({ user, sidebar, main, preview }) {
  const mainPadding = { xs: "12px 12px 0", sm: "20px 20px 0", md: user ? 5 : "50px 60px" };
  const sidebarSticky = {
    position: { lg: "sticky" },
    top: { lg: 0 },
    height: { lg: "calc(100vh - 80px)" },
    alignSelf: { lg: "start" },
  };

  if (user) {
    return (
      <Grid container sx={{ width: "100%" }}>
        <Grid size={{ xs: 12, lg: 3 }} sx={{ ...sidebarSticky, display: { xs: "none", lg: "block" } }}>
          {sidebar}
        </Grid>
        <Grid size={{ xs: 12, lg: 9 }} sx={{ minWidth: 0 }}>
          <Stack spacing={3.75} sx={{ p: mainPadding }}>
            {main}
          </Stack>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container sx={{ width: "100%" }}>
      <Grid size={{ xs: 12, lg: 3 }} sx={{ ...sidebarSticky, display: { xs: "none", lg: "block" } }}>
        {sidebar}
      </Grid>

      <Grid size={{ xs: 12, lg: 9 }} sx={{ minWidth: 0, order: { xs: 1, lg: 2 } }}>
        <Stack spacing={3.75} sx={{ p: mainPadding, zIndex: 2 }}>
          {main}
        </Stack>
      </Grid>

      {preview && (
        <Grid size={12} sx={{ display: "none", order: 2 }}>
          <Box sx={{ position: "relative", zIndex: 2 }}>{sidebar}</Box>
        </Grid>
      )}
    </Grid>
  );
}

export function PortalCard({ children, sx, dark }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: 1.5,
        border: 1,
        borderColor: dark ? "rgba(99,102,241,0.15)" : "#e2e8f0",
        bgcolor: dark ? "rgba(15,23,42,0.6)" : "#fff",
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
}

export function SplitGrid({ children, columns = "1.2fr 0.8fr", sx }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: columns },
        gap: { xs: 3, md: 4 },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function StatGrid({ children, sx }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(auto-fit, minmax(220px, 1fr))" },
        gap: 2.5,
        mb: 3.75,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function WelcomeHeader({ displayName, subtitle, themeColors }) {
  return (
    <Stack
      direction={{ xs: "column", sm: "row" }}
      spacing={1.5}
      sx={{ justifyContent: "space-between", alignItems: { xs: "flex-start", sm: "center" }, mb: { xs: 2.5, md: 3.75 } }}
    >
      <Box sx={{ width: "100%" }}>
        <Typography variant="h4" sx={{ fontWeight: 900, fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2.2rem" }, lineHeight: 1.2 }}>
          Welcome back, {displayName}!
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.6, fontSize: { xs: 13, sm: 14 }, mt: 0.5 }}>
          {subtitle}
        </Typography>
      </Box>
      <Chip
        icon={<Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#10b981" }} />}
        label="LU SECURED"
        sx={{ bgcolor: "#10b98115", border: "1px solid #10b98133", color: "#10b981", fontWeight: 700, fontSize: { xs: 11, sm: 12 }, alignSelf: { xs: "flex-start", sm: "center" } }}
      />
    </Stack>
  );
}
